import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M24 L24.04 — Rule decisions search.
 *
 * Reads `GET /v1/rule-executions/logs` (one row per rule evaluation) with
 * outcome / simulation filters and a transaction-reference search.
 */
interface DecisionRow {
  readonly executionId: string;
  readonly ruleCode: string | null;
  readonly ruleVersion: string | null;
  readonly transactionReference: string;
  readonly outcome: string;
  readonly matched: boolean;
  readonly executionTimeMs: number;
  readonly isSimulation: boolean;
  readonly executedBy: string;
  readonly executedAt: string;
}

const OUTCOMES = ['', 'MATCH', 'NO_MATCH', 'ERROR'];
const SIMS = [
  { key: 'simAll', value: '' },
  { key: 'simLive', value: 'false' },
  { key: 'simSimulation', value: 'true' },
];
const PAGE = 25;

export function RuleDecisionsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<DecisionRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState('');
  const [isSimulation, setIsSimulation] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (outcome) params.set('outcome', outcome);
    if (isSimulation) params.set('isSimulation', isSimulation);
    if (txnRef.trim()) params.set('transactionReference', txnRef.trim());
    try {
      const r = await apiRequest<{ data: DecisionRow[]; total: number }>(
        `/v1/rule-executions/logs?${params.toString()}`, { token },
      );
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token, outcome, isSimulation, txnRef, offset]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="rule-decisions">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m24.decisions.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m24.decisions.subtitle')}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label htmlFor="outcome-filter" style={{ fontSize: 12 }}>{t('m24.decisions.outcome')}</label>
        <select id="outcome-filter" aria-label="outcome-filter" value={outcome}
                onChange={(e) => { setOffset(0); setOutcome(e.target.value); }}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {OUTCOMES.map((o) => <option key={o || 'all'} value={o}>{o || t('m24.decisions.outcomeAll')}</option>)}
        </select>

        <label htmlFor="sim-filter" style={{ fontSize: 12 }}>{t('m24.decisions.type')}</label>
        <select id="sim-filter" aria-label="sim-filter" value={isSimulation}
                onChange={(e) => { setOffset(0); setIsSimulation(e.target.value); }}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {SIMS.map((s) => <option key={s.value || 'all'} value={s.value}>{t(`m24.decisions.${s.key}`)}</option>)}
        </select>

        <input aria-label="txn-ref" placeholder={t('m24.decisions.txnRefPlaceholder')} value={txnRef}
               onChange={(e) => setTxnRef(e.target.value)}
               style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        <button type="button" data-testid="search-btn" onClick={() => { setOffset(0); void load(); }}
                style={{ padding: '5px 12px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m24.decisions.search')}</button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('m24.decisions.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="decisions-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m24.decisions.empty')}</p>
      ) : (
        <>
          <table aria-label="decisions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colExecuted')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colRule')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colTxnRef')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colOutcome')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colMatched')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colTime')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m24.decisions.colType')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.executionId} data-testid={`decision-row-${d.executionId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: '8px 4px' }}>{at(d.executedAt)}</td>
                  <td style={{ padding: '8px 4px' }}>{d.ruleCode ?? '—'}{d.ruleVersion ? ` v${d.ruleVersion}` : ''}</td>
                  <td style={{ padding: '8px 4px' }}>{d.transactionReference}</td>
                  <td style={{ padding: '8px 4px' }}>{d.outcome}</td>
                  <td style={{ padding: '8px 4px' }}>{d.matched ? t('m24.decisions.yes') : t('m24.decisions.no')}</td>
                  <td style={{ padding: '8px 4px' }}>{t('m24.decisions.timeMs', { ms: d.executionTimeMs })}</td>
                  <td style={{ padding: '8px 4px' }}>{d.isSimulation ? t('m24.decisions.simulation') : t('m24.decisions.live')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <button type="button" data-testid="prev-page" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE))}
                    style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m24.decisions.prev')}</button>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('m24.decisions.pageInfo', { from: offset + 1, to: offset + rows.length, total })}</span>
            <button type="button" data-testid="next-page" disabled={offset + PAGE >= total}
                    onClick={() => setOffset(offset + PAGE)}
                    style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m24.decisions.next')}</button>
          </div>
        </>
      )}
    </main>
  );
}
